import { useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { FileImage, Plus, Upload } from 'lucide-react';
import EvidenceCard from './EvidenceCard';

export const MAX_PHOTOS = 8;

/**
 * Multi-photo upload gallery. Drives all interactions: pick more files,
 * remove a card, drag-and-drop. Always renders the "add more" tile when
 * below the cap, OR the empty-state drop zone when no photos exist yet.
 */
export default function EvidenceGallery({
  entries = [],            // [{file, previewUrl}]
  coords = [],             // [{lat,lng,source?,...} | null | undefined]
  uploadProgress = [],     // [0..1 | null]
  onAdd,                   // (FileList | File[]) => void
  onRemove,                // (idx) => void
  disabled = false,
  size = 'normal',
  readOnly = false,
  extractingIndices = null, // Set<number> — indices currently in stamp extraction
  offenderIndices = null,   // Set<number> — indices flagged as the GPS outlier
}) {
  const inputRef = useRef(null);

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const onSelect = (e) => {
    if (e.target.files?.length) onAdd(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (disabled || readOnly) return;
    const files = Array.from(e.dataTransfer.files || []).filter((f) => /^image\//.test(f.type));
    if (files.length) onAdd(files);
  };

  // Empty state: full-size drop zone (preserves the existing pattern from
  // the old ImageDropZone component so muscle memory carries over).
  if (entries.length === 0 && !readOnly) {
    return (
      <div
        className="evidence-dropzone"
        onClick={openPicker}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={onDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            openPicker();
          }
        }}
        aria-label="بارگذاری تصاویر بازرسی"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={onSelect}
          hidden
          disabled={disabled}
        />
        <div className="evidence-dropzone-icon">
          <FileImage size={40} strokeWidth={1.4} />
        </div>
        <div className="evidence-dropzone-primary">تصاویر را اینجا رها کنید</div>
        <div className="evidence-dropzone-secondary">یا برای انتخاب از سیستم کلیک کنید - تا ۸ عکس از زوایای مختلف</div>
        <div className="evidence-dropzone-format">JPG · PNG · WebP - حداکثر ۲۰ مگابایت هر فایل</div>
        <span className="evidence-dropzone-pick">
          <Upload size={15} />
          انتخاب فایل
        </span>
      </div>
    );
  }

  const canAdd = !readOnly && !disabled && entries.length < MAX_PHOTOS;

  return (
    <div className={`evidence-gallery evidence-gallery-${size}`}>
      <AnimatePresence>
        {entries.map((entry, i) => (
          <EvidenceCard
            key={entry.previewUrl}
            entry={entry}
            index={i}
            coords={coords[i]}
            uploadProgress={uploadProgress[i]}
            onRemove={readOnly ? null : () => onRemove(i)}
            size={size}
            extracting={extractingIndices?.has(i) || false}
            isOffender={offenderIndices?.has(i) || false}
          />
        ))}
      </AnimatePresence>

      {canAdd && (
        <button
          type="button"
          className="evidence-add-tile"
          onClick={openPicker}
          aria-label="افزودن عکس بیشتر"
        >
          <Plus size={28} strokeWidth={1.5} />
          <span>افزودن عکس</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={onSelect}
        hidden
      />
    </div>
  );
}
